/**
 * Valid RCRAInfo Biennial Report codes for source, form, and management method.
 * These codes are used to validate waste stream data before flat-file generation.
 *
 * Source: RCRAInfo Biennial Report File Specifications (2025)
 * See: ~/Downloads/Source Codes, Form Codes, Mgmt Method Codes.pdf
 */

/**
 * Source codes describe the type of process or activity from which hazardous waste was generated.
 */
export const VALID_SOURCE_CODES = [
  // On-going Production and Service Processes
  "G01", // Dip, flush or spray rinsing
  "G02", // Stripping and acid or caustic cleaning
  "G03", // Plating and phosphating
  "G04", // Etching
  "G05", // Metal forming and treatment
  "G06", // Painting and coating
  "G07", // Product and by-product processing
  "G08", // Removal of spent process liquids or catalysts
  "G09", // Other production or service-related processes

  // Intermittent Events or Processes
  "G11", // Discarding off-specification/out-of-date chemicals
  "G12", // Lagoon or sediment dragout
  "G13", // Cleaning out process equipment
  "G14", // Removal of tank sludge, sediments, or slag
  "G15", // Process equipment change-out
  "G16", // Oil changes and filter or battery replacement
  "G17", // Subpart K laboratory waste clean-out
  "G19", // Other one-time or intermittent processes

  // Pollution Control and Waste Management
  "G21", // Air pollution control devices
  "G22", // Laboratory analytical wastes
  "G23", // Wastewater treatment
  "G24", // Solvent or product distillation
  "G25", // Treatment, disposal, or recycling of hazardous wastes
  "G26", // Leachate collection
  "G27", // Treatment or recovery of universal waste

  // Spills and Accidental Releases
  "G31", // Accidental contamination
  "G32", // Cleanup of spill residues
  "G33", // Leak collection and floor sweeping
  "G39", // Other cleanup of current contamination

  // Remediation of Past Contamination
  "G41", // Closure of hazardous waste management unit under RCRA
  "G42", // Corrective action at solid waste management unit under RCRA
  "G43", // Remedial action or emergency response under Superfund
  "G44", // Cleanup under State or voluntary program
  "G45", // Cleanup of underground storage tank
  "G49", // Other remediation

  // LQG from VSQGs
  "G51", // Hazardous wastes received by LQG from VSQGs

  // Not Physically Generated On-site
  "G61", // Received from off-site for storage/bulking
  "G62", // Hazardous waste received from outside U.S.
  "G76", // Evaluated hazardous waste pharmaceuticals
  "G77", // Airbag waste received from airbag waste handlers
] as const;

export type SourceCode = (typeof VALID_SOURCE_CODES)[number];

/**
 * Form codes describe the general physical and chemical characteristics of hazardous waste.
 */
export const VALID_FORM_CODES = [
  // Mixed Media/Debris/Devices
  "W001", // Lab packs (non-acute)
  "W002", // Contaminated debris
  "W004", // Lab packs (acute)
  "W005", // Waste pharmaceuticals
  "W006", // Airbag waste
  "W301", // Contaminated soil
  "W309", // Batteries, battery parts
  "W310", // Filters, solid adsorbents, ion exchange resins
  "W320", // Electrical devices (lamps, thermostats, CRTs)
  "W512", // Sediment or lagoon dragout
  "W801", // Compressed gases

  // Inorganic Liquids
  "W101", // Very dilute aqueous waste (>99% water)
  "W103", // Spent concentrated acid (5%+)
  "W105", // Acidic aqueous wastes (<5% acid)
  "W107", // Aqueous waste containing cyanides
  "W110", // Caustic aqueous waste without cyanides
  "W113", // Other aqueous waste or wastewaters
  "W117", // Waste liquid mercury
  "W119", // Other inorganic liquid

  // Organic Liquids
  "W200", // Still bottoms in liquid form
  "W202", // Concentrated halogenated solvent
  "W203", // Concentrated non-halogenated solvent
  "W204", // Concentrated halogenated/non-halogenated solvent mixture
  "W205", // Oil-water emulsion or mixture
  "W206", // Waste oil managed as hazardous waste
  "W209", // Paint, ink, lacquer, or varnish
  "W210", // Reactive or polymerizable organic liquids
  "W211", // Paint thinner or petroleum distillates
  "W219", // Other organic liquid

  // Inorganic Solids
  "W303", // Ash
  "W304", // Slags, drosses, solid thermal residues
  "W307", // Metal scale, filings, scrap
  "W312", // Cyanide or metal cyanide bearing solids
  "W316", // Metal salts or chemicals (no cyanides)
  "W319", // Other inorganic solids

  // Organic Solids
  "W401", // Pesticide solids
  "W403", // Solid resins, plastics, polymerized organics
  "W405", // Explosives or reactive organic solids
  "W406", // Dried paint
  "W409", // Other organic solids

  // Inorganic Sludges
  "W501", // Lime and/or metal hydroxide sludges
  "W503", // Gypsum sludges
  "W504", // Other sludges from wastewater treatment
  "W505", // Metal bearing sludges
  "W506", // Cyanide-bearing sludges
  "W519", // Other inorganic sludges

  // Organic Sludges
  "W603", // Oily sludge
  "W604", // Paint or ink sludges, still bottoms in sludge form
  "W606", // Resins, tars, polymer or tarry sludge
  "W609", // Other organic sludge
] as const;

export type FormCode = (typeof VALID_FORM_CODES)[number];

/**
 * Management method codes describe the type of hazardous waste management system used.
 */
// Descriptions for each code live in MANAGEMENT_METHOD_CODE_DESCRIPTIONS below;
// the Record type keeps the two in lockstep (a code here without a description,
// or vice versa, is a compile error).
export const VALID_MANAGEMENT_METHOD_CODES = [
  // Reclamation and Recovery
  "H010",
  "H011",
  "H015",
  "H020",
  "H039",
  "H050",
  "H061",

  // Destruction or Treatment Prior to Disposal
  "H040",
  "H041",
  "H042",
  "H070",
  "H081",
  "H090",
  "H100",
  "H110",
  "H113",
  "H120",
  "H121",
  "H122",
  "H129",

  // Disposal
  "H130",
  "H131",
  "H132",
  "H134",
  "H136",
  "H137",

  // Transfer Off-site
  "H141",

  // Transfer Off-site for Reclamation/Recovery
  "S010",
  "S011",
  "S015",
  "S020",
  "S039",
  "S050",
  "S061",

  // Transfer Off-site for Destruction/Treatment
  "S040",
  "S041",
  "S042",
  "S070",
  "S081",
  "S090",
  "S100",
  "S110",
  "S113",
  "S120",
  "S121",
  "S122",
  "S129",

  // Transfer Off-site for Disposal
  "S130",
  "S131",
  "S132",
  "S134",
] as const;

export type ManagementMethodCode = (typeof VALID_MANAGEMENT_METHOD_CODES)[number];

/**
 * Human-readable descriptions for RCRA management method codes, so UIs can show
 * "H141 · Storage/bulking and transfer" instead of a bare code. Keys are the
 * uppercase MMCs; use {@link getManagementMethodCodeDescription} for lookups.
 */
export const MANAGEMENT_METHOD_CODE_DESCRIPTIONS: Record<ManagementMethodCode, string> = {
  H010: "Metals recovery",
  H011: "Mercury recovery",
  H015: "Airbag waste deployment/deactivation + metals recovery",
  H020: "Solvents recovery",
  H039: "Other recovery or reclamation",
  H050: "Energy recovery at this site",
  H061: "Fuel blending prior to energy recovery at another site",
  H040: "Incineration - thermal destruction",
  H041: "Open burning/open detonation",
  H042: "Thermal desorption",
  H070: "Chemical treatment",
  H081: "Biological treatment",
  H090: "Polymerization",
  H100: "Physical treatment only",
  H110: "Stabilization prior to land disposal",
  H113: "Stabilization to remove characteristics",
  H120: "Combination of chemical/biological/physical treatment",
  H121: "Neutralization only",
  H122: "Evaporation",
  H129: "Other treatment (no onsite disposal)",
  H130: "Surface impoundment (closed as landfill)",
  H131: "Land treatment or application",
  H132: "Landfill",
  H134: "Deepwell or underground injection",
  H136: "Discharge to sewer/POTW",
  H137: "Discharge with NPDES permit",
  H141: "Storage/bulking and transfer (no treatment/disposal)",
  S010: "Transfer for metals recovery",
  S011: "Transfer for mercury recovery",
  S015: "Transfer for airbag waste + metals recovery",
  S020: "Transfer for solvents recovery",
  S039: "Transfer for other recovery",
  S050: "Transfer for energy recovery",
  S061: "Transfer for fuel blending",
  S040: "Transfer for incineration",
  S041: "Transfer for open burning/detonation",
  S042: "Transfer for thermal desorption",
  S070: "Transfer for chemical treatment",
  S081: "Transfer for biological treatment",
  S090: "Transfer for polymerization",
  S100: "Transfer for physical treatment",
  S110: "Transfer for stabilization (land disposal)",
  S113: "Transfer for stabilization (remove characteristics)",
  S120: "Transfer for combination treatment",
  S121: "Transfer for neutralization",
  S122: "Transfer for evaporation",
  S129: "Transfer for other treatment",
  S130: "Transfer for surface impoundment",
  S131: "Transfer for land treatment",
  S132: "Transfer for landfill",
  S134: "Transfer for deepwell/underground injection",
};

const DESCRIPTION_BY_CODE = new Map<string, string>(
  Object.entries(MANAGEMENT_METHOD_CODE_DESCRIPTIONS)
);

/**
 * Returns the human-readable description for a management method code, or
 * undefined for an unknown/blank code. Case-insensitive.
 */
export function getManagementMethodCodeDescription(
  code: string | null | undefined
): string | undefined {
  if (!code) return undefined;
  return DESCRIPTION_BY_CODE.get(code.toUpperCase());
}

/**
 * Helper to check if a code is a valid source code
 */
export function isValidSourceCode(code: string): code is SourceCode {
  return VALID_SOURCE_CODES.includes(code as SourceCode);
}

/**
 * Helper to check if a code is a valid form code
 */
export function isValidFormCode(code: string): code is FormCode {
  return VALID_FORM_CODES.includes(code as FormCode);
}

/**
 * Helper to check if a code is a valid management method code.
 *
 * When sourceCode is provided:
 * - If sourceCode is G25, the code must be a valid MMC AND not H141 (or any excluded on-site method)
 * - Otherwise, the code just needs to be a valid MMC
 */
export function isValidManagementMethodCode(
  code: string,
  sourceCode?: string | null
): code is ManagementMethodCode {
  if (!VALID_MANAGEMENT_METHOD_CODES.includes(code as ManagementMethodCode)) {
    return false;
  }
  if (sourceCode === "G25" && isExcludedOnsiteManagementMethod(code)) {
    return false;
  }
  return true;
}

/**
 * Form codes that require splitting waste stream rows by effective management method code.
 * W001 (lab packs, non-acute) and W004 (lab packs, acute) produce one GM1 row per unique
 * effective MMC, whereas all other form codes produce a single row per waste stream.
 */
export const SPLIT_BY_MMC_FORM_CODES = ["W001", "W004"] as const;

/**
 * Checks whether a form code requires splitting waste stream data by management method code.
 * Used to determine grouping behavior in export queries and destination filtering.
 */
export function isSplitByManagementMethod(formCode: string | null | undefined): boolean {
  if (!formCode) return false;
  return SPLIT_BY_MMC_FORM_CODES.includes(formCode as (typeof SPLIT_BY_MMC_FORM_CODES)[number]);
}

/**
 * Management method codes that are NOT allowed for on-site management.
 *
 * Per RCRAInfo spec (GM1-050, GM5-030):
 * - H141 is deprecated after 2025 cycle
 * - S-codes are for off-site transfer only, not on-site management
 *
 * These codes indicate waste was stored/bulked and transferred to another site,
 * which is incompatible with on-site management or source code G25.
 */
export const EXCLUDED_ONSITE_MANAGEMENT_METHODS = [
  "H141", // Storage/bulking and transfer (deprecated)
  "S010", // Transfer for metals recovery
  "S011", // Transfer for mercury recovery
  "S015", // Transfer for airbag waste + metals recovery
  "S020", // Transfer for solvents recovery
  "S039", // Transfer for other recovery
  "S040", // Transfer for incineration
  "S041", // Transfer for open burning/detonation
  "S042", // Transfer for thermal desorption
  "S050", // Transfer for energy recovery
  "S061", // Transfer for fuel blending
  "S070", // Transfer for chemical treatment
  "S081", // Transfer for biological treatment
  "S090", // Transfer for polymerization
  "S100", // Transfer for physical treatment
  "S110", // Transfer for stabilization (land disposal)
  "S113", // Transfer for stabilization (remove characteristics)
  "S120", // Transfer for combination treatment
  "S121", // Transfer for neutralization
  "S122", // Transfer for evaporation
  "S129", // Transfer for other treatment
  "S130", // Transfer for surface impoundment
  "S131", // Transfer for land treatment
  "S132", // Transfer for landfill
  "S134", // Transfer for deepwell/underground injection
] as const;

export type ExcludedOnsiteManagementMethod = (typeof EXCLUDED_ONSITE_MANAGEMENT_METHODS)[number];

/**
 * Checks if a management method code is excluded from on-site management.
 *
 * Per RCRAInfo spec (GM1-050, GM5-030), H141 and S-codes cannot be used
 * for on-site management or when source code is G25.
 */
export function isExcludedOnsiteManagementMethod(
  code: string
): code is ExcludedOnsiteManagementMethod {
  return EXCLUDED_ONSITE_MANAGEMENT_METHODS.includes(code as ExcludedOnsiteManagementMethod);
}

/**
 * Source/form/management method codes that require a comment/explanation in the RCRAInfo Biennial Report.
 *
 * Per RCRAInfo File Specification, certain characteristic source/form codes require
 * an explanation in the public_notes field (GM1 row) describing WHY the waste
 * exhibits that characteristic.
 *
 * See https://linear.app/encamp/issue/ENG-13346/waste-staff-bulk-generation-of-flat-files-for-rcrainfo#comment-62b65f74 for list.
 */
export const SOURCE_CODES_REQUIRING_COMMENT = [
  "G09",
  "G19",
  "G39",
  "G49",
  "W119",
  "W219",
  "W319",
  "W409",
  "W519",
  "W609",
  "H039",
  "H129",
];

export const PHRM_CODES = ["PHRM", "PHARM"];

/**
 * Checks if a code is a PHRM/PHARM code (pharmaceutical waste).
 */
export function isPhrmCode(code: string): boolean {
  const upperCode = code.toUpperCase();
  return PHRM_CODES.some((phrmCode) => upperCode.startsWith(phrmCode));
}

/**
 * Checks if a federal waste code is valid for standard format.
 * Standard codes: 4 characters with 1-Alpha Character and 3-Numeric Characters
 * Alpha character always precedes. Either D, F, K, U or P (e.g. D001, F005, U154, etc.)
 */
export function isValidStandardFederalWasteCode(value: string): boolean {
  const upperCode = value.toUpperCase();
  const federalWasteCodeRegex = /^[DFKUP]\d{3}$/;
  return federalWasteCodeRegex.test(upperCode);
}

/**
 * Context for checking if a federal waste code is exportable.
 * PHRM/PHARM codes are exportable when source code G61 and form code W005 are present.
 */
export type ExportableFederalCodeContext = {
  sourceCodes?: string[];
  formCodes?: string[];
};

/**
 * Checks if a federal waste code should be included in RCRAInfo flat files.
 * A code is exportable if:
 * 1. It is a valid standard federal waste code (D/F/K/U/P + 3 digits), OR
 * 2. It is a PHRM/PHARM code with source code G61 and form code W005
 *
 * Invalid codes are filtered out during export rather than causing validation errors.
 *
 * @param code The federal waste code to check
 * @param context Optional - when provided, PHRM codes are exportable if sourceCodes includes G61 and formCodes includes W005
 */
export function isExportableFederalCode(
  code: string,
  context?: ExportableFederalCodeContext
): boolean {
  if (isPhrmCode(code)) {
    // PHRM codes are exportable when accompanied by G61 source and W005 form
    const hasG61 = context?.sourceCodes?.some((c) => c?.toUpperCase() === "G61") ?? false;
    const hasW005 = context?.formCodes?.some((c) => c?.toUpperCase() === "W005") ?? false;
    return hasG61 && hasW005;
  }

  // Must be a valid standard federal waste code
  return isValidStandardFederalWasteCode(code);
}

/**
 * Checks if a waste code is an OUTS (out-of-state) code that should be excluded.
 * OUTS codes can appear with or without a state prefix (e.g., "OUTS004H" or "TX-OUTS004H").
 * Also catches truncated variants like "TX-OUT" without the trailing 's'.
 */
export function isOutsCode(code: string): boolean {
  const lowerCode = code.toLowerCase();
  return lowerCode.startsWith("outs") || lowerCode.includes("-outs") || lowerCode.includes("-out");
}

// The biennial GM3 export gate (`isStateCodeReportableOnBiennial`) lives in
// `encamp-waste-data-access` and reads the canonical classifier's
// `reportableOnFederalBiennial` field directly. It replaced the hand-maintained
// `isExportableStateCode` mirror that used to live here; `encamp-shared` cannot
// call the classifier (that edge would be circular), which is why the gate
// moved to the package that already depends on `encamp-compliance-engine`.
